/**
 * SemanticStatusBadge — Phase 4 Part 2.
 *
 * Consumes a raw backend emoji glyph (e.g. `mortgageTier.emoji`) and
 * renders it via the design-system `Badge` primitive as a semantic
 * icon + accessible label. NEVER emits the raw glyph into the DOM.
 *
 * Props:
 *   • emoji    — the raw backend glyph string (may be undefined/null)
 *   • label    — optional secondary label rendered next to the icon
 *                (defaults to the semantic descriptor's `ariaLabel`
 *                for screen readers only — visible label is opt-in)
 *   • hideLabel— if true, only the icon renders (label goes to sr-only)
 *   • size     — passthrough to Badge
 *   • ...rest  — passthrough to Badge (className, data-testid, etc.)
 */

import React from "react";
import { Badge } from "@/design-system/components/Badge";
import { emojiToSemanticToken } from "@/design-system/utils/emojiToSemanticToken";

export function SemanticStatusBadge({
  emoji,
  label,
  hideLabel = false,
  size = "sm",
  className,
  "data-testid": testId,
  ...rest
}) {
  const { tone, Icon, ariaLabel } = emojiToSemanticToken(emoji);
  const visibleLabel = hideLabel ? null : label;
  const srLabel = label || ariaLabel;
  return (
    <Badge
      tone={tone}
      size={size}
      className={className}
      data-testid={testId}
      {...rest}
    >
      <Icon
        className="h-3 w-3 shrink-0"
        aria-hidden={visibleLabel ? true : undefined}
        aria-label={visibleLabel ? undefined : srLabel}
      />
      {visibleLabel ? <span>{visibleLabel}</span> : <span className="sr-only">{srLabel}</span>}
    </Badge>
  );
}

export default SemanticStatusBadge;
